import * as React from 'react';
import { connect } from 'react-redux';
import { AppStates } from '../../reducers/app';
import { CurrentTool, UIState } from '../../reducers/ui/ui';
import ObjectDrawingToolIcon from '../drawing/object-drawing/ObjectDrawingToolIcon';
import FreehandPenToolIcon from '../drawing/freehand-pen/FreehandPenToolIcon';
import HanderasingToolIcon from '../drawing/HanderasingToolIcon';
import TextboxToolIcon from '../textbox/TextboxToolIcon';
import { getBasePath } from '../../utils/browser';
import IconImage from '../common/IconImage';
import ToolIconButton from '../common/ToolIconButton';

interface CurrentToolIconProps {
  readonly uiState: UIState;
}

const containerStyle: React.CSSProperties = {
  width: '52px',
  height: '52px',
  backgroundColor: '#ababab',
  border: 'solid #fff 2px',
  boxSizing: 'border-box',
  margin: '6px 6px 6px 6px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '5px',
};

const CurrentToolIconContainer: React.SFC = (props) => {
  return (
    <div style={containerStyle}>
      {props.children}
    </div>
  );
};

interface CurrentToolIconImageProps {
  readonly imagePath: string;
  readonly style?: React.CSSProperties;
  readonly title?: string;
}

const CurrentToolIconImage: React.SFC<CurrentToolIconImageProps> = (props) => {
  const { imagePath } = props;
  const baseDir = getBasePath();
  const imgSrc = `${baseDir}/${imagePath}_out.png`;
  return (
    <IconImage src={imgSrc} />
  );
};

// develop
const markerColorName = ['赤', '青', '黄色', '緑', '黒'];

const CurrentToolIcon: React.SFC<CurrentToolIconProps> = React.memo((props) => {
  const { uiState } = props;

  switch (uiState.currentTool) {
    case CurrentTool.MultiroleLink: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/mylink/mylink"
            title="マイリンク" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.ImagePasting: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/attachment/attachment"
            title="画像貼り付け" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.ObjectDrawing: {
      return (
        <CurrentToolIconContainer>
          <ObjectDrawingToolIcon
            style={{ margin: 0 }}
            buttonNumber={uiState.objectDrawing.selected}
            title={`図形描画${uiState.objectDrawing.selected + 1}`} />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.ObjectSelection: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/object_selection/object_selection"
            title="オブジェクト選択" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.ObjectRemoval: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/eraser/eraser"
            title="オブジェクト消去" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.AllErasing: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/trashbox/trashbox"
            title="ゴミ箱" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.Textbox: {
      return (
        <CurrentToolIconContainer>
          <TextboxToolIcon
            style={{ margin: 0 }}
            buttonNumber={uiState.textbox.selected}
            title={`テキストボックス${uiState.textbox.selected + 1}`} />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.Touch: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/zoompan/zoompan"
            title="ズーム/パン(+紙面操作)" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.Mouse: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/txt_select/txt_select"
            title="紙面操作" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.RegionZoom: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/spot_zoom/spot_zoom"
            title="スポットズーム" />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.FreeHandPen: {
      return (
        <CurrentToolIconContainer>
          <FreehandPenToolIcon
            style={{ margin: 0 }}
            buttonNumber={uiState.freehandPen.selected}
            title={`ペン${uiState.freehandPen.selected + 1}`} />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.Handerasing: {
      return (
        <CurrentToolIconContainer>
          <HanderasingToolIcon
            style={{ margin: 0 }}
            width={uiState.handerasing.width} />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.LearningPen: {
      return (
        <CurrentToolIconContainer>
          <ToolIconButton
            text="暗記ペン"
            margin={false}
            onClick={() => {}}
          />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.MarkerAppend: {
      let imageName = 'marker_black';
      switch (uiState.marker.selectedColorIndex) {
        case 0:
          imageName = 'marker_black';
          break;
        case 1:
          imageName = 'marker_blue';
          break;
        case 2:
          imageName = 'marker_green';
          break;
        case 3:
          imageName = 'marker_red';
          break;
        case 4:
          imageName = 'marker_yellow';
          break;
      }
      const imgSrc = `images/tool-icons/new/${imageName}/${imageName}`;

      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath={imgSrc}
            title={`マーカー(${markerColorName[uiState.marker.selectedColorIndex]})`} />
        </CurrentToolIconContainer>
      );
    }
    case CurrentTool.MarkerDelete: {
      return (
        <CurrentToolIconContainer>
          <CurrentToolIconImage
            imagePath="images/tool-icons/new/eraser/eraser"
            title="消しゴム(マーカー)" />
        </CurrentToolIconContainer>
      );
    }
  }
});

const mapStateToProps = (appStates: AppStates) => ({ uiState: appStates.ui });

export default connect(mapStateToProps)(CurrentToolIcon);
